import {
  Button,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
  Container,
  Grid,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Stack,
} from "@mui/material";
import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { createBooking, resetBookingState } from "../../State/Booking/Action";
import { useMyContext } from "../../store/ContextApi";
import moment from "moment";
import { useNavigate } from "react-router-dom";
import { getEstimatedPriceFiltered } from "../../State/EstimatedPrice/Action";
import DateTimeButton from "../Tools/DateTimeButton";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import {
  DEFAULT_WASH_TYPE,
  VEHICLE_WASH_TYPE_OPTIONS,
  WASH_TYPE_LABELS,
  formatWashTypeKey,
} from "../../constants/washTypes";

export default function Booking() {
  const [dateTime, setDateTime] = useState();
  const [errorMessage, setErrorMessage] = useState("");
  const [successMessage, setSuccessMessage] = useState("");
  const [openSuccessDialog, setOpenSuccessDialog] = useState(false);
  const [openErrorDialog, setOpenErrorDialog] = useState(false);
  const storedVehicle =
    localStorage.getItem("vehicleType") === "BIKE" ? "BIKE" : "CAR";
  const storedWash = localStorage.getItem("washType");
  const initialWash =
    storedWash && VEHICLE_WASH_TYPE_OPTIONS[storedVehicle]?.includes(storedWash)
      ? storedWash
      : DEFAULT_WASH_TYPE;
  const [vehicle, setVehicle] = useState(storedVehicle);
  const [wash, setWash] = useState(initialWash);
  const defaultSeater = storedVehicle === "CAR" ? "FIVE_SEATER" : "TWO_SEATER";
  const [seater, setSeater] = useState(defaultSeater);
  const dispatch = useDispatch();
  const { currentUser } = useMyContext();
  const { booking } = useSelector((store) => store);
  const { estimatedPrice } = useSelector((store) => store);
  const navigate = useNavigate();
  const [phoneError, setPhoneError] = useState("");

  const handleSubmit = (event) => {
    event.preventDefault();
    setErrorMessage("");
    setSuccessMessage("");

    if (!dateTime) {
      setErrorMessage("Please choose your slot");
      setOpenErrorDialog(true);
      return;
    }

    const formData = new FormData(event.target);

    const booking = {
      customerName: currentUser.fullName,
      email: currentUser.email,
      phone: formData.get("phone"),
      address: formData.get("address"),
      scheduledDate: `${dateTime}`,
      washType: formatWashTypeKey(wash, vehicle),
      vehicleType: vehicle,
      vehicle,
      seater: seater,
    };

    if (isValidForm(booking)) {
      const inputRequest = { booking, setSuccessMessage, setErrorMessage };
      dispatch(createBooking(inputRequest));
    }
  };

  useEffect(() => {
    if (booking.bookingResonse?.success) {
      setSuccessMessage("Booking created successfully");
      setOpenSuccessDialog(true);

      // Clear Redux store after a short delay to prevent message on reload
      setTimeout(() => {
        dispatch(resetBookingState());
      }, 500);
    } else if (booking.error) {
      setErrorMessage("Failed to create booking");
      setOpenErrorDialog(true);

      setTimeout(() => {
        dispatch(resetBookingState());
      }, 500);
    }
  }, [booking.bookingResonse, booking.error, dispatch]);

  useEffect(() => {
    if (!VEHICLE_WASH_TYPE_OPTIONS[vehicle]?.includes(wash)) {
      const fallback =
        VEHICLE_WASH_TYPE_OPTIONS[vehicle]?.[0] || DEFAULT_WASH_TYPE;
      setWash(fallback);
      return;
    }
  }, [vehicle, wash]);

  useEffect(() => {
    localStorage.setItem("vehicleType", vehicle);
  }, [vehicle]);

  useEffect(() => {
    localStorage.setItem("washType", wash);
  }, [wash]);

  useEffect(() => {
    const day = moment(dateTime || new Date()).format("dddd").toUpperCase();
    dispatch(getEstimatedPriceFiltered(vehicle, day, wash, seater));
  }, [dispatch, vehicle, wash, dateTime, seater]);

  const isValidForm = (data) => {
    if (!data.phone) {
      setErrorMessage("Enter a valid phone number");
      setOpenErrorDialog(true);
      return false;
    }
    if (!data.address) {
      setErrorMessage("Address is required");
      setOpenErrorDialog(true);
      return false;
    }
    return true;
  };

  const handleCloseSuccessDialog = () => {
    setOpenSuccessDialog(false);
    navigate("/");
  };

  const handleCloseErrorDialog = () => {
    setOpenErrorDialog(false);
  };

  const handleVehicleChange = (value) => {
    setVehicle(value);
    if (value === "CAR") {
      setSeater("FIVE_SEATER");
    } else {
      setSeater("TWO_SEATER");
    }
    const fallback =
      VEHICLE_WASH_TYPE_OPTIONS[value]?.[0] || DEFAULT_WASH_TYPE;
    setWash(fallback);
  };

  return (
    <Container maxWidth="md" sx={{ mt: 5 }}>
      <Paper elevation={4} sx={{ p: 4, borderRadius: 3 }}>
        <Typography
          variant="h5"
          fontWeight="bold"
          textAlign={"center"}
          gutterBottom
        >
          Book your Wash
        </Typography>
        {currentUser ? (
          <Typography variant="subtitle1" color="text.secondary">
            Name: <strong>{currentUser.fullName}</strong>
          </Typography>
        ) : (
          <Typography>Loading...</Typography>
        )}
        <form onSubmit={handleSubmit}>
          <Grid container spacing={3} sx={{ mt: 2 }}>
            <Grid item xs={12}>
              <InputLabel>Vehicle Type</InputLabel>
              <Select
                fullWidth
                value={vehicle}
                onChange={(e) => handleVehicleChange(e.target.value)}
                name="vehicleType"
              >
                <MenuItem value="CAR">CAR</MenuItem>
                <MenuItem value="BIKE">BIKE</MenuItem>
              </Select>
            </Grid>
            <Grid item xs={12}>
              <InputLabel>Wash Type</InputLabel>
              <Select
                fullWidth
                value={wash}
                onChange={(e) => setWash(e.target.value)}
                name="wash"
              >
                {VEHICLE_WASH_TYPE_OPTIONS[vehicle]?.map((type) => (
                  <MenuItem key={type} value={type}>
                    {WASH_TYPE_LABELS[type] || type}
                  </MenuItem>
                ))}
              </Select>
            </Grid>
            <Grid item xs={12}>
              {vehicle === "CAR" && (
                <Stack direction="row" spacing={2} justifyContent="center">
                  <Button
                    variant="contained"
                    color={seater === "FIVE_SEATER" ? "primary" : "inherit"}
                    size="small"
                    onClick={() => setSeater("FIVE_SEATER")}
                  >
                    5 Seater
                  </Button>
                  <Button
                    variant="contained"
                    color={
                      seater === "SIX_SEVEN_SEATER" ? "primary" : "inherit"
                    }
                    size="small"
                    onClick={() => setSeater("SIX_SEVEN_SEATER")}
                  >
                    6–7 Seater
                  </Button>
                  <Button
                    variant="contained"
                    color={
                      seater === "EIGHT_AND_MORE_SEATER" ? "primary" : "inherit"
                    }
                    size="small"
                    onClick={() => setSeater("EIGHT_AND_MORE_SEATER")}
                  >
                    8 & More Seater
                  </Button>
                </Stack>
              )}
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Phone Number"
                name="phone"
                defaultValue={currentUser?.phone}
                variant="outlined"
                error={!!phoneError}
                helperText={phoneError}
                sx={{ mb: 2 }}
                inputProps={{ maxLength: 10 }}
                onChange={(e) => {
                  const value = e.target.value
                    .replace(/[^0-9]/g, "")
                    .slice(0, 10);
                  e.target.value = value;
                  if (value.length !== 10) {
                    setPhoneError("Phone number must be exactly 10 digits");
                  } else {
                    setPhoneError("");
                  }
                }}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Address"
                name="address"
                defaultValue={currentUser?.address}
                multiline
                rows={3}
                variant="outlined"
              />
            </Grid>
            {/* <Grid item xs={12}>
              <DateTimeDropDown setDateTime={setDateTime} delay={2} />
            </Grid> */}
            <Grid item xs={12}>
              <DateTimeButton setDateTime={setDateTime} delay={2} />
            </Grid>

            <Grid
              container
              justifyContent="center"
              alignItems="center"
              sx={{ pt: 2 }}
            >
              <Grid item>
                <Typography
                  variant="subtitle1"
                  textAlign="center"
                  fontWeight="bold"
                  sx={{
                    display: "inline-block",
                    backgroundColor: "rgba(0, 0, 0, 0.05)",
                    padding: "6px 12px",
                    borderRadius: "4px",
                    color: "#333",
                    fontSize: "1rem",
                  }}
                >
                  Estimated Price:{" "}
                  <Typography
                    component="span"
                    variant="h6"
                    fontWeight="bold"
                    color="primary"
                  >
                    ₹{Math.round(estimatedPrice?.estimatedPriceObj?.total) || 0}
                  </Typography>
                </Typography>
              </Grid>
            </Grid>
            <Grid item xs={12} textAlign="center" sx={{ mt: 2 }}>
              <Typography variant="body2" color="textSecondary">
                Note: <strong>Harrier</strong>, <strong>MG Hector</strong>,{" "}
                <strong>Thar</strong>, and <strong>Thar Roxx</strong> will be
                treated as 7-seater vehicles.
              </Typography>
            </Grid>

            <Grid item xs={12} textAlign="center">
              <Button
                type="submit"
                variant="contained"
                color="primary"
                size="large"
              >
                Book Now
              </Button>
            </Grid>
          </Grid>
        </form>
        {/* Success Dialog */}
        <Dialog
          open={openSuccessDialog}
          onClose={handleCloseSuccessDialog}
          maxWidth="xs"
          fullWidth
        >
          <DialogTitle
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: 1,
            }}
          >
            <CheckCircleOutlineIcon sx={{ color: "green", fontSize: 40 }} />
            <Typography variant="h6" fontWeight="bold" color="green">
              Success
            </Typography>
          </DialogTitle>
          <DialogContent>
            <Box
              display="flex"
              flexDirection="column"
              alignItems="center"
              textAlign="center"
              p={2}
            >
              <Typography variant="body1">
                ✅ Your booking has been <b>successfully created!</b>
              </Typography>
            </Box>
          </DialogContent>
          <DialogActions sx={{ justifyContent: "center" }}>
            <Button
              variant="contained"
              color="success"
              onClick={handleCloseSuccessDialog}
            >
              🎉 Okay
            </Button>
          </DialogActions>
        </Dialog>

        {/* Error Dialog */}
        <Dialog
          open={openErrorDialog}
          onClose={handleCloseErrorDialog}
          maxWidth="xs"
          fullWidth
        >
          <DialogTitle
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: 1,
            }}
          >
            <ErrorOutlineIcon sx={{ color: "red", fontSize: 40 }} />
            <Typography variant="h6" fontWeight="bold" color="red">
              Error
            </Typography>
          </DialogTitle>
          <DialogContent>
            <Box
              display="flex"
              flexDirection="column"
              alignItems="center"
              textAlign="center"
              p={2}
            >
              <Typography variant="body1" color="error">
                ❌ {errorMessage}
              </Typography>
            </Box>
          </DialogContent>
          <DialogActions sx={{ justifyContent: "center" }}>
            <Button
              variant="contained"
              color="error"
              onClick={handleCloseErrorDialog}
            >
              🔄 Try Again
            </Button>
          </DialogActions>
        </Dialog>
      </Paper>
    </Container>
  );
}
